'use client';

import { c as _c } from "react/compiler-runtime";
import cx from 'classix';
import { useAtomValue } from 'jotai';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { type MouseEvent, useState } from 'react';
import { FaHeart } from 'react-icons/fa6';
import ListingAddress from '@/features/listing/components/listing-card/listing-address/listing-address';
import { type ListingCardViewBaseProps } from '@/features/listing/components/listing-card/listing-card-types';
import ListingRate from '@/features/listing/components/listing-card/listing-rate/listing-rate';
import createListingBookmark from '@/features/listing/requests/create-listing-bookmark';
import deleteListingBookmark from '@/features/listing/requests/delete-listing-bookmark';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { VerificationStatus } from '@/features/user/types/user-id-verification-types';
import { Link, useRouter } from '@/i18n/routing';
import { ROUTER_LINKS } from '@/shared/constants/links-constants';
import Avatar from '@/ui/avatar/avatar';
import Badge from '@/ui/badge/badge';
import styles from './listing-card-view-list.module.scss';
export default function ListingCardViewList(props) {
  const $ = _c(39);
  const {
    isHighPriority,
    isListingLinkDisabled: t0,
    isListingPerson,
    link: t1,
    listing,
    showBookmarkButton,
    showCheckStatus,
    showListingUser,
    showMissingData
  } = props;
  const isListingLinkDisabled = t0 === undefined ? false : t0;
  const link = t1 === undefined ? null : t1;
  const router = useRouter();
  const appFlyerMobileBookmarkLink = useAppFlyerLink("mobile", "mobileListingBookmark", ROUTER_LINKS.phoneAuth);
  const translatesListingCard = useTranslations("listing.card");
  const translatesVerifications = useTranslations("global.verifications");
  const loggedUser = useAtomValue(loggedUserAtom);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      [VerificationStatus.New]: "gray",
      [VerificationStatus.Passed]: "green",
      [VerificationStatus.Pending]: "orange",
      [VerificationStatus.Rejected]: "red",
      [VerificationStatus.Error]: "red"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const listingStatusToBadgeColor = t2 as const;
  const [isListingBookmarked, setIsListingBookmarked] = useState(listing.isBookmarked);
  let t3;
  if ($[1] !== appFlyerMobileBookmarkLink || $[2] !== isListingBookmarked || $[3] !== listing.id || $[4] !== listing.listingTypeId || $[5] !== loggedUser || $[6] !== router) {
    t3 = event => {
      event.stopPropagation();
      event.preventDefault();
      if (loggedUser) {
        if (isListingBookmarked) {
          setIsListingBookmarked(false);
          deleteListingBookmark(listing.id, listing.listingTypeId).catch(_temp);
        } else {
          setIsListingBookmarked(true);
          createListingBookmark(listing.id, listing.listingTypeId).catch(_temp2);
        }
      } else {
        router.push(appFlyerMobileBookmarkLink);
      }
    };
    $[1] = appFlyerMobileBookmarkLink;
    $[2] = isListingBookmarked;
    $[3] = listing.id;
    $[4] = listing.listingTypeId;
    $[5] = loggedUser;
    $[6] = router;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleBookmarkClick = t3;
  let t4;
  if ($[8] !== isHighPriority || $[9] !== listing.addressFormatted || $[10] !== listing.addressVerificationStatus || $[11] !== listing.images[0].pathSmall || $[12] !== listing.isDataMissing || $[13] !== listing.listingTypeId || $[14] !== listing.rates || $[15] !== listing.title || $[16] !== showCheckStatus || $[17] !== showMissingData || $[18] !== translatesListingCard || $[19] !== translatesVerifications) {
    t4 = () => <><div className={styles.listingCardViewList_imageContainer}><figure><Image className={cx(styles.listingCardViewList_image)} src={listing.images[0].pathSmall.toString()} alt={listing.title} sizes="60px" fill={true} priority={isHighPriority} fetchPriority={isHighPriority ? "high" : "low"} loading={isHighPriority ? "eager" : "lazy"} /></figure></div><div className={styles.listingCardViewList_info}><h3 className={styles.listingCardViewList_title}>{listing.title}</h3><ListingAddress address={listing.addressFormatted} listingTypeId={listing.listingTypeId} showListingType={false} /><ListingRate rate={listing.rates} showPerMonth={false} />{showCheckStatus && <Badge color={listingStatusToBadgeColor[listing.addressVerificationStatus]}>{translatesVerifications(`address-check-${listing.addressVerificationStatus}`)}</Badge>}{showMissingData && listing.isDataMissing && <Badge>{translatesListingCard("complete-listing")}</Badge>}</div></>;
    $[8] = isHighPriority;
    $[9] = listing.addressFormatted;
    $[10] = listing.addressVerificationStatus;
    $[11] = listing.images[0].pathSmall;
    $[12] = listing.isDataMissing;
    $[13] = listing.listingTypeId;
    $[14] = listing.rates;
    $[15] = listing.title;
    $[16] = showCheckStatus;
    $[17] = showMissingData;
    $[18] = translatesListingCard;
    $[19] = translatesVerifications;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const renderListingBody = t4;
  const t5 = !isListingPerson && showCheckStatus && styles[`listingCardViewList__${listing.addressVerificationStatus}Status`];
  let t6;
  if ($[21] !== t5) {
    t6 = cx(styles.listingCardViewList, t5);
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== isListingLinkDisabled || $[24] !== link || $[25] !== listing.id || $[26] !== listing.title || $[27] !== renderListingBody) {
    t7 = isListingLinkDisabled ? <div className={styles.listingCardViewList_body}>{renderListingBody()}</div> : <Link className={styles.listingCardViewList_body} href={link ?? `${ROUTER_LINKS.listings}/${String(listing.id)}`} title={listing.title} prefetch={false}>{renderListingBody()}</Link>;
    $[23] = isListingLinkDisabled;
    $[24] = link;
    $[25] = listing.id;
    $[26] = listing.title;
    $[27] = renderListingBody;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] !== handleBookmarkClick || $[30] !== isListingBookmarked || $[31] !== listing.user || $[32] !== showBookmarkButton || $[33] !== showListingUser) {
    t8 = (showBookmarkButton ?? showListingUser) && <div className={styles.listingCardViewList_side}>{showListingUser && <Link className={styles.listingCardViewList_user} href={`${ROUTER_LINKS.profile}/${String(listing.user.id)}`} prefetch={false}><Avatar imageSrc={listing.user.avatar.pathSmall} alt={listing.user.firstName} size={26} view="circle" /></Link>}{showBookmarkButton && <button className={styles.listingCardViewList_bookmark} aria-label="favorite" onClick={handleBookmarkClick}><FaHeart className={cx(styles.listingCardViewList_bookmarkIcon, isListingBookmarked && styles.listingCardViewList_bookmarkIcon__isBookmarked)} /></button>}</div>;
    $[29] = handleBookmarkClick;
    $[30] = isListingBookmarked;
    $[31] = listing.user;
    $[32] = showBookmarkButton;
    $[33] = showListingUser;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t6 || $[36] !== t7 || $[37] !== t8) {
    t9 = <article className={t6}>{t7}{t8}</article>;
    $[35] = t6;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  return t9;
}
function _temp2() {
  return null;
}
function _temp() {
  return null;
}